import React, { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Calendar } from "lucide-react";
import axios from "axios";

const Footer = () => {
  // Form state
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(
        "https://apigulmohar.dodunsoftsolutions.com/api/contact-form",
        {
          ...formData,
          company: "Real Estate DB",
        }
      );
      setSuccess(true);
      setFormData({
        fullName: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
      });
    } catch (error) {
      console.error("Error submitting form:", error);
    }
    setLoading(false);
  };

  return (
    <section
      className="py-24 bg-white dark:bg-gray-900 relative overflow-hidden"
      id="contact"
    >
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?q=80&w=2670&auto=format&fit=crop"
          alt="Luxury Property Backdrop"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 to-slate-700/80 dark:from-gray-900/95 dark:to-gray-800/90"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Updated Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl dark:shadow-blue-900/10 p-8 md:p-10"
            data-aos="fade-right"
          >
            <div className="mb-8">
              <h3 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                Schedule a Viewing
              </h3>
              <div className="w-16 h-1 bg-slate-600 dark:bg-slate-400 mb-4"></div>
              <p className="text-gray-600 dark:text-gray-300">
                Interested in experiencing our properties firsthand? Fill out
                the form below, and our luxury real estate consultant will
                contact you to arrange a private viewing.
              </p>
            </div>

            <form className="space-y-6" onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label
                    htmlFor="fullName"
                    className="block text-gray-700 dark:text-gray-200 font-medium mb-2"
                  >
                    Full Name
                  </label>
                  <input
                    type="text"
                    id="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500 dark:focus:ring-slate-400 focus:border-transparent dark:text-gray-100"
                    placeholder="John Doe"
                    required
                  />
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-gray-700 dark:text-gray-200 font-medium mb-2"
                  >
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500 dark:focus:ring-slate-400 focus:border-transparent dark:text-gray-100"
                    placeholder="john@example.com"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label
                    htmlFor="phone"
                    className="block text-gray-700 dark:text-gray-200 font-medium mb-2"
                  >
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500 dark:focus:ring-slate-400 focus:border-transparent dark:text-gray-100"
                    placeholder="+91 XXXXX XXXXX"
                    required
                  />
                </div>

                <div>
                  <label
                    htmlFor="subject"
                    className="block text-gray-700 dark:text-gray-200 font-medium mb-2"
                  >
                    Property Interest
                  </label>
                  <select
                    id="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500 dark:focus:ring-slate-400 focus:border-transparent appearance-none dark:text-gray-100"
                    required
                  >
                    <option value="">Select a Property</option>
                    <option value="Malabar Hill Residences">
                      Malabar Hill Residences
                    </option>
                    <option value="Evergreen Heights">Evergreen Heights</option>
                    <option value="Cedar Sanctuary">Cedar Sanctuary</option>
                    <option value="Other">Other / Not Sure Yet</option>
                  </select>
                </div>
              </div>

              <div>
                <label
                  htmlFor="message"
                  className="block text-gray-700 dark:text-gray-200 font-medium mb-2"
                >
                  Your Message
                </label>
                <textarea
                  id="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="4"
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500 dark:focus:ring-slate-400 focus:border-transparent resize-none dark:text-gray-100"
                  placeholder="I'm interested in scheduling a viewing for..."
                  required
                ></textarea>
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-slate-600 hover:bg-slate-700 dark:bg-slate-700 dark:hover:bg-slate-600 text-white py-4 rounded-lg font-medium transition-colors duration-300 flex items-center justify-center"
                >
                  {loading ? "Submitting..." : "Schedule My Viewing"}
                  {!loading && <Calendar size={18} className="ml-2" />}
                </button>
              </div>

              {success && (
                <p className="text-green-500 text-center mt-4">
                  Thank you! We will get in touch shortly.
                </p>
              )}
            </form>
          </motion.div>

          {/* Content Side */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            data-aos="fade-right"
          >
            <div className="text-white space-y-6">
              <h2 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
                Your Dream Home in{" "}
                <span className="text-blue-300 dark:text-blue-400">
                  Himachal Pradesh
                </span>{" "}
                Awaits
              </h2>

              <div className="w-20 h-1 bg-blue-300 dark:bg-blue-400 mb-6"></div>

              {/* Contact Info Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
                <div className="bg-white/10 dark:bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/20 dark:border-white/10">
                  <div className="flex items-start">
                    <div className="w-12 h-12 rounded-full bg-blue-500/20 dark:bg-blue-500/10 flex items-center justify-center flex-shrink-0 mr-4 mb-4 sm:mb-0">
                      <Phone
                        size={24}
                        className="text-blue-300 dark:text-blue-400"
                      />
                    </div>
                    <div>
                      <h4 className="text-xl font-bold text-white mb-2">
                        Call Us
                      </h4>
                      <p className="text-white/80 dark:text-white/70 mb-4">
                        Our team is available 7 days a week
                      </p>
                      <a
                        href="tel:+91-xxxxxxxxxx"
                        className="text-blue-300 dark:text-blue-400 font-medium hover:text-blue-200 dark:hover:text-blue-300 transition-colors"
                      >
                        +91-xxxxxxxxxx
                      </a>
                    </div>
                  </div>
                </div>

                <div className="bg-white/10 dark:bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/20 dark:border-white/10">
                  <div className="flex  items-start">
                    <div className="w-12 h-12 rounded-full bg-blue-500/20 dark:bg-blue-500/10 flex items-center justify-center flex-shrink-0 mr-4 mb-4 sm:mb-0">
                      <Mail
                        size={24}
                        className="text-blue-300 dark:text-blue-400"
                      />
                    </div>
                    <div className="min-w-0 w-full break-words">
                      <h4 className="text-xl font-bold text-white mb-2">
                        Email Us
                      </h4>
                      <p className="text-white/80 dark:text-white/70 mb-4">
                        For inquiries and information
                      </p>
                      <a
                        href="mailto:info@devbhoomigroup.com"
                        className="text-blue-300 dark:text-blue-400 font-medium hover:text-blue-200 dark:hover:text-blue-300 transition-colors break-all"
                      >
                        info@devbhoomigroup.com
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Visit Us Card */}
              <div className="bg-white/10 dark:bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/20 dark:border-white/10 mt-6">
                <div className="flex items-start">
                  <div className="w-12 h-12 rounded-full bg-blue-500/20 dark:bg-blue-500/10 flex items-center justify-center flex-shrink-0 mr-4">
                    <MapPin
                      size={24}
                      className="text-blue-300 dark:text-blue-400"
                    />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-white mb-2">
                      Visit Our Office
                    </h4>
                    <p className="text-white/80 dark:text-white/70 mb-4">
                      Corporate Headquarters
                    </p>
                    <address className="text-blue-300 dark:text-blue-400 font-medium not-italic">
                      Dev Bhoomi Group 8 mile stone, Behdala Una, Himachal
                      Pradesh 174306
                    </address>
                  </div>
                </div>
              </div>

              {/* Google Maps */}
              <div className="mt-8 rounded-xl overflow-hidden h-64 relative">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d32723.697734887915!2d76.32583631041322!3d31.427298223999504!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x391acf7372e92127%3A0x190e1127f9bcee3!2sDev%20Bhoomi%20Group%20companies!5e0!3m2!1sen!2sin!4v1745478025319!5m2!1sen!2sin"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Dev Bhoomi Group location"
                  className="z-0"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-slate-900/60 dark:bg-gray-900/60 backdrop-blur-sm mt-20 py-8 relative z-10 border-t border-white/10">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="text-white mb-4 md:mb-0">
              <h3 className="text-2xl font-bold text-white mb-1">
                Dev Bhoomi Real Estates{" "}
                <span className="text-blue-300 dark:text-blue-400">DB</span>
              </h3>
              <p className="text-white/70 text-sm">
                Luxury Real Estate in Himachal Pradesh since 2005
              </p>
            </div>

            <div className="text-white/70 text-sm">
              &copy; {new Date().getFullYear()} Dev Bhoomi Group. All rights
              reserved.
            </div>
          </div>
        </div>
      </footer>
    </section>
  );
};

export default Footer;
